import { useCallback, useEffect, useState } from 'react'

export type Theme = 'dark' | 'light'
const STORAGE_KEY = 'sms-theme'

function currentTheme(): Theme {
  // index.html already resolved this before first paint; read it back so the
  // hook and the DOM never disagree on the initial render.
  const applied = document.documentElement.dataset.theme
  return applied === 'light' ? 'light' : 'dark'
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(currentTheme)

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme === 'dark' ? '#0b0b0e' : '#f7f6f3')
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      /* storage blocked — the theme still applies for this session */
    }
  }, [theme])

  // Follow the OS only while the visitor hasn't expressed a preference.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = (event: MediaQueryListEvent) => {
      try {
        if (localStorage.getItem(STORAGE_KEY)) return
      } catch {
        /* fall through and follow the OS */
      }
      setTheme(event.matches ? 'light' : 'dark')
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'))
  }, [])

  return { theme, setTheme, toggleTheme }
}
